/**
 * 云服务类别 产品实例列表
 *
 * @format
 */

import React, { FC, useState, useEffect, useContext } from 'react'
import styled from 'styled-components'
import { _$, Button, Icon, Tag, Tooltip, Message, Card, Tabs, Tab } from 'kpc-react'
import { useHistory } from 'react-router'
import RegionBtn from 'src/components/RegionBtn'
import { useCopyToClipboard } from 'react-use'
import CloudListTableCopyTips from './CloudListTableCopyTips'
import TablePage from 'src/components/TablePage'
import useFormTable from 'src/hooks/useFormTable'
import { InstanceObject, DrawerType } from 'src/pages/MonitorCloud/constants'
import { CustomInstanceFormValue, customInstanceInitFormValue } from '../constants'
import { request } from 'src/utils'
import { CountContext } from 'src/pages/MonitorCloud/MonitorCloudList'
import { MonitorSearch } from 'src/components'
import CustomInstanceDialog from 'src/containers/MonitorCloud/CloudListTable/CustomInstanceDialog'
import Api from 'src/api/ApiCloud'
import useGlobalData from 'src/hooks/useGlobalData'
import useNavRegion from 'src/hooks/useNavRegion'

interface CloudListTableProps {
  className?: string
  // region: string
  openDrawer: (instance: InstanceObject, type: DrawerType) => void // 打开【监控项】、【告警策略】抽屉的方法
  showCustomInstance: boolean // 是否展示【自定义聚合实例】按钮
  openBatchExport: () => void // 打开【批量下载弹窗】的弹窗
  checkedData?: any[] // 勾选的实例
  setCheckedData?: (cheked: any[]) => void // 勾选实例change时
}

interface TableSearchParams {
  ProductType: string
  RegionKey: string
  Query: string
  InstanceType?: string
}

const CloudListTable: FC<CloudListTableProps> = React.memo(
  ({ className, openDrawer, showCustomInstance }) => {
    const { productType, topType, childTypeValue, tabsList, setTabsValue } =
      useContext(CountContext)
    // table请求params
    const [formParams, setFormParams] = useState<TableSearchParams>({
      ProductType: productType,
      RegionKey: '',
      Query: ''
    })
    // 产品菜单
    const productMenu = useGlobalData('productMenu')
    // 自定义聚合实例
    const [customInstanceDialogVisible, setCustomInstanceDialogVisible] = useState<boolean>(false)
    const [customInstanceDialogValue, setCustomInstanceDialogValue] =
      useState<CustomInstanceFormValue>(customInstanceInitFormValue)
    // copy hooks
    const [copyState, copyToClipboard] = useCopyToClipboard()
    const history = useHistory()

    // 控制台region
    const region = useNavRegion()

    // useFormTable
    const { run, result, pageState, loading, total } = useFormTable(
      Api.getInstanceList,
      {
        params: { ...formParams },
        manual: true
      },
      data => {
        return {
          tableData: data?.resourceList || [],
          totalCount: data?.totalCount
        }
      }
    )

    // 删除实例组
    const deleteInstance = async instanceId => {
      if (instanceId) {
        const result = await request('/monitorapi/host/delete', {
          method: 'POST',
          data: {
            productType,
            instanceIdList: [instanceId]
          }
        })
        if (result?.code === 'Success') {
          run({ ...formParams })
        }
      }
    }

    // 临时解决kpc组件库中tabs卸载后还在调用的问题
    const refreshScroll = Tabs.prototype._refreshScroll
    Tabs.prototype._refreshScroll = function () {
      if (this.destroyed) return
      refreshScroll.call(this)
    }

    // table column
    const getScheme = () => {
      const hideDrawer = [48, 67, 50, 56, 73, 81, 70, 84, 109]
      const schemeConfig = {
        instanceName: {
          title: _$('实例名称'),
          minWidth: 300,
          template({ instanceName, instanceId, instanceType }) {
            const isHostGroup = instanceType === 2
            return (
              <div className="table-host-name">
                {isHostGroup && (
                  <Tag type="primary" size="mini">
                    {_$('聚合实例')}
                  </Tag>
                )}
                <p
                  className={
                    isHostGroup ? 'primary-color cursor-pointer text-overflow' : 'text-overflow'
                  }
                  onClick={() => {
                    isHostGroup && opencCstomInstance(instanceId)
                  }}
                >
                  {instanceName}
                </p>
                {!isHostGroup && (
                  <>
                    <CloudListTableCopyTips instanceId={instanceId} />
                    <i
                      className="icon icon-fuzhi2 primary-color copy-icon cursor-pointer"
                      onClick={() => copyToClipboard(instanceId)}
                    ></i>
                  </>
                )}
              </div>
            )
          }
        },
        monitorStatus: {
          title: _$('监控状态'),
          template({ monitorStatus }) {
            switch (monitorStatus) {
              case 0:
                return <span className="table-status table-status-normal">{_$('初始化中')}</span>
              case 1:
                return <span className="table-status table-status-normal">{_$('监控中')}</span>
              case 2:
                return <span className="table-status table-status-abnormal">{_$('异常')}</span>
              default:
                return ''
            }
          }
        },
        instanceIP: {
          title: _$('IP地址'),
          template({ instanceIP, instanceType }) {
            return (
              <div>
                <span>{instanceIP}</span>
                {/* {instanceType === 2 && instanceList.length > 0 && (
                  <Tooltip
                    canHover
                    theme="light"
                    b-content={
                      <ul>
                        {instanceList.map((item, index) => (
                          <li key={item + index}>{item}</li>
                        ))}
                      </ul>
                    }
                  >
                    <Icon
                      className="ion-help-circled cursor-pointer"
                      style={{ fontSize: '14px', marginLeft: '5px' }}
                    ></Icon>
                  </Tooltip>
                )} */}
              </div>
            )
          }
        },
        monitorMetricsNumber: {
          title: _$('监控项'),
          template({ monitorMetricsNumber, instanceName, instanceId }) {
            return (
              <a
                onClick={() => {
                  openDrawer({ instanceName, instanceId }, 'monitor')
                }}
                className="primary-color"
              >
                {monitorMetricsNumber}
              </a>
            )
          }
        },
        thresholdPoliciesNumber: {
          title: _$('告警策略'),
          template({ thresholdPoliciesNumber, instanceName, instanceId }) {
            return (
              <a
                onClick={() => {
                  openDrawer({ instanceName, instanceId }, 'alarm')
                }}
                className="primary-color"
              >
                {thresholdPoliciesNumber}
              </a>
            )
          }
        },
        active: {
          title: _$('操作'),
          template({ instanceName, instanceType, instanceId }) {
            return (
              <div className="ksc-space">
                <a
                  className="primary-color"
                  onClick={event => {
                    const find = productMenu.find(
                      menu => menu.productType === parseInt(productType)
                    )
                    if (find) {
                      const idCode = encodeURIComponent(instanceId)
                      const nameCode = encodeURIComponent(instanceName)
                      // const productName = encodeURIComponent(find.title)
                      const region = formParams.RegionKey
                      history.push(
                        `/monitor/list/overview/${productType}/${idCode}/${nameCode}/${
                          topType || productType
                        }/${region}`
                      )
                    }
                  }}
                >
                  {_$('监控图表')}
                </a>
                {instanceType === 2 && (
                  <Tooltip
                    content={`${_$('确定删除')}${instanceName}？`}
                    confirm
                    theme="light"
                    trigger="click"
                    onOk={() => {
                      deleteInstance(instanceId)
                    }}
                  >
                    <a className="primary-color">{_$('删除')}</a>
                  </Tooltip>
                )}
              </div>
            )
          }
        }
      }
      if (productType !== '0') {
        delete schemeConfig.monitorStatus
      }
      if (topType && hideDrawer.includes(parseInt(topType))) {
        delete schemeConfig.monitorMetricsNumber
        delete schemeConfig.thresholdPoliciesNumber
      }
      return schemeConfig
    }

    // 初始化、切换产品类型
    useEffect(() => {
      if (productType !== undefined) {
        const newParams: TableSearchParams = {
          ...formParams,
          ProductType: productType === '109' ? productType : topType || productType,
          RegionKey: region,
          Query: ''
        }
        setFormParams(newParams)
      }
    }, [productType, topType, region])

    // 筛选条件有变化的时候 重新请求列表
    useEffect(() => {
      const { ProductType, RegionKey } = formParams
      if (ProductType && RegionKey) {
        run({ ...formParams, PageIndex: 1 })
      }
    }, [formParams])

    // 复制成功/失败后提示
    useEffect(() => {
      const { error, value } = copyState
      if (value) {
        if (error) {
          Message.error(error)
        } else {
          Message.success(`${_$('复制成功')} ${value}`)
        }
      }
    }, [copyState])

    // 打开【自定义聚合实例】弹窗
    const opencCstomInstance = async instanceId => {
      if (instanceId && typeof instanceId === 'string') {
        const result = await request(Api.queryAggregateInstace, {
          method: 'GET',
          data: {
            InstanceId: instanceId,
            ProductType: productType
          }
        })
        setCustomInstanceDialogValue(result?.data?.data)
      } else {
        setCustomInstanceDialogValue(customInstanceInitFormValue)
      }
      setCustomInstanceDialogVisible(true)
    }

    // 关闭【自定义聚合实例】弹窗
    const closeDialog = (_c, value) => {
      if (!value) {
        setCustomInstanceDialogValue(customInstanceInitFormValue)
        setCustomInstanceDialogVisible(value)
      }
    }

    return (
      <>
        {productType && (
          <Card className={className}>
            {/* 大区 */}
            {/* <RegionBtn regionChange={regionChange} /> */}
            {tabsList && !!tabsList.length ? (
              <Tabs
                type="no-border-card"
                value={topType}
                on$change-value={(_c, value) => {
                  const newParams: TableSearchParams = {
                    ...formParams,
                    ProductType: value,
                    Query: ''
                  }
                  setTabsValue(productType, value)
                  setFormParams(newParams)
                }}
              >
                {tabsList.map(({ childType, title }) => (
                  <Tab key={childType} value={childType}>
                    {_$(title)}
                  </Tab>
                ))}
              </Tabs>
            ) : (
              ''
            )}
            {/* 新建告警策略 + 搜索  */}
            <div className="table-header">
              <div className="left-button">
                <Button
                  type="primary"
                  onClick={event => {
                    history.push(
                      `/monitor/alarmServer/rule/create/${
                        childTypeValue?.childType || topType || productType
                      }`
                    )
                  }}
                >
                  <Icon className="ion-plus-round" />
                  {_$('新建告警策略')}
                </Button>
                {showCustomInstance && (
                  <Button type="primary" onClick={opencCstomInstance}>
                    <Icon className="ion-plus-round" />
                    {_$('自定义聚合实例')}
                  </Button>
                )}
                {/* <Button disabled={!checkedData.length} onClick={openBatchExport}>
                  批量导出
                </Button> */}

                {productType === '0' ? (
                  <p>
                    {_$('异常状态的云主机，可能未启动')}
                    <a href="//docs.ksyun.com/documents/6787" className="primary-color">
                      {_$('监控Agent')}
                    </a>
                    {_$('导致无监控数据。')}
                  </p>
                ) : (
                  ''
                )}
              </div>
              <div className="right-search">
                <MonitorSearch
                  placeholder={_$('输入实例ID、实例名称、实例信息')}
                  value={formParams.Query}
                  onChange={value => {
                    const newParams: TableSearchParams = {
                      ...formParams,
                      Query: value
                    }
                    setFormParams(newParams)
                  }}
                />
                <Icon
                  className="ion-refresh cursor-pointer"
                  onClick={() => {
                    setFormParams({ ...formParams, Query: '' })
                  }}
                />
              </div>
            </div>
            {/*  table  */}
            <TablePage
              tableProps={{
                data: result,
                scheme: getScheme(),
                resizable: true,
                checkType: 'none',
                rowKey: value => value.instanceId
                // rowCheckable: false,
                // checkedData,
                // rowKey: item => item,
                // 'on$change-checked': (_c, value) => {
                //   setCheckedData(value)
                // }
              }}
              pageProps={{ total, ...pageState }}
              loading={loading}
              run={run}
            />
          </Card>
        )}

        {showCustomInstance && (
          <CustomInstanceDialog
            visible={customInstanceDialogVisible}
            handleChange={closeDialog}
            defaultFormValue={customInstanceDialogValue}
            updateTable={run}
          />
        )}
      </>
    )
  }
)

export default styled(CloudListTable)`
  background: #fff;
  min-height: calc(100vh - 120px);
  .k-tabs {
    margin-bottom: 16px;
  }
  .k-body {
    padding: 20px;
    p {
      margin-bottom: 0;
    }
  }
  .table-header {
    /* margin-top: 16px; */
    display: flex;
    justify-content: space-between;
    .left-button {
      display: flex;
      align-items: center;
      .k-btn {
        margin-right: 8px;
      }
      .p {
        margin-left: 12px;
      }
    }
    .right-search {
      display: flex;
      align-items: center;
      .k-input {
        margin-right: 16px;
      }
    }
  }
  .table-page {
    tr {
      .table-host-name {
        display: flex;
        align-items: center;
        .k-tag {
          margin-right: 5px;
        }
        .copy-icon {
          font-size: 14px;
          margin-left: 5px;
          opacity: 0;
          transition: opacity 0.15s ease-out;
        }
      }
      .table-status {
        position: relative;
        padding-left: 10px;
        &:before {
          content: ' ';
          position: absolute;
          left: 0;
          top: 50%;
          margin-top: -2px;
          width: 4px;
          height: 4px;
          border-radius: 50%;
        }
        &.table-status-normal:before {
          background-color: #36b342;
        }
        &.table-status-abnormal:before {
          background-color: #ff9800;
        }
      }
      &:hover {
        .copy-icon {
          opacity: 1;
        }
      }
    }
  }
`
